import { baseURL } from '@/libs/api';
import { queryClient } from '@/libs/query-client';

export class HttpError<T = unknown> extends Error {
  constructor(
    public status: number,
    public data: T,
  ) {
    super(`Request failed with status ${status}`);
    this.name = 'HttpError';
  }
}

export function isHttpError<T = unknown>(error: unknown): error is HttpError<T> {
  return error instanceof HttpError;
}

export type ErrorType<Error> = HttpError<Error>;

// 同時に複数の401が発生した場合、token refreshは1回だけ実行する
let refreshPromise: Promise<string> | null = null;

// トークン更新が接続元ごとの回数の上限(429)に達し、やり直しても通らなかった。
// リフレッシュトークンは有効なままなので、ログイン状態は破棄しない
class TokenRefreshRateLimitedError extends Error {
  constructor() {
    super('Token refresh rate limited');
    this.name = 'TokenRefreshRateLimitedError';
  }
}

// 429 のときにトークン更新を試す回数(初回を含む)
const REFRESH_MAX_ATTEMPTS = 3;
// Retry-After を読めないときに待つ秒数
const DEFAULT_RETRY_AFTER_SECONDS = 5;
// 回数の上限は 1 分の窓で数えるので、それより長くは待たない
const MAX_RETRY_AFTER_SECONDS = 60;

const retryAfterMs = (response: Response): number => {
  const seconds = Number(response.headers.get('Retry-After'));
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_SECONDS * 1000;
  return Math.min(seconds, MAX_RETRY_AFTER_SECONDS) * 1000;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');

  for (let attempt = 1; ; attempt++) {
    const response = await fetch(new URL('/api/v2/accounts/token/refresh', baseURL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (response.status === 429) {
      if (attempt >= REFRESH_MAX_ATTEMPTS) throw new TokenRefreshRateLimitedError();
      await wait(retryAfterMs(response));
      continue;
    }
    if (!response.ok) throw new Error('Token refresh failed');

    const data = (await response.json()) as { access: string };
    const newAccessToken = data.access;
    localStorage.setItem('accessToken', newAccessToken);
    return newAccessToken;
  }
};

function clearAuthAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  queryClient.setQueryData(['auth'], { isAuthenticated: false });
  window.location.href = '/signin';
}

function getHeaders(headers?: HeadersInit): HeadersInit {
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    // アニマルオフィスと同じ認証・エンドポイントを使うため、操作の発生元をサーバーへ申告する
    'X-Hanly-Client': 'warehouse',
  };

  const token = localStorage.getItem('accessToken');
  if (token) {
    defaultHeaders.Authorization = `Bearer ${token}`;
  }

  return { ...defaultHeaders, ...headers };
}

// orval用のカスタムインスタンス
export const customInstance = async <T>(url: string, options: RequestInit): Promise<T> => {
  const requestUrl = new URL(url, baseURL);
  const fetchWithHeaders = () =>
    fetch(requestUrl, {
      ...options,
      headers: getHeaders(options.headers),
    });

  let response = await fetchWithHeaders();

  // 401: トークンリフレッシュを試みる
  if (response.status === 401) {
    try {
      // 既にrefresh中なら同じPromiseを待つ
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch (error) {
      if (error instanceof TokenRefreshRateLimitedError) {
        throw new HttpError(429, null);
      }
      clearAuthAndRedirect();
      const errorData = await response.json().catch(() => null);
      throw new HttpError(401, errorData);
    }
    response = await fetchWithHeaders();
  }

  if (response.status === 401) {
    clearAuthAndRedirect();
    const errorData = await response.json().catch(() => null);
    throw new HttpError(401, errorData);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new HttpError(response.status, errorData);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};
